import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { T } from 'react-translator-component';

// Referencia estable para evitar bucle en useEffect cuando no hay pasajeros
const EMPTY_PASSENGERS = [];

const QuestionPaxQtySelector = ({
    values = [],
    settings = {},
    paxQuestion = null,
    currency = null,
    addon = null,
    language = 'en',
    checkin = null,
    onChange = null
}) => {
    const [selectedQty, setSelectedQty] = useState('');
    const [selectedPassengers, setSelectedPassengers] = useState({});

    // Si viene paxQuestion (de Settings), usar ese
    const questionConfig = paxQuestion || (settings?.questions?.find(q => q.type === 'pax-qty-selector'));

    // Obtener el número total de pasajeros del checkin (siempre array)
    const totalPassengers = useMemo(() => {
        const pax = checkin?.passengers;
        return Array.isArray(pax) ? pax : [];
    }, [checkin?.passengers]);

    // Obtener el valor actual del estado
    const currentValue = useMemo(() => {
        if (!values || values.length === 0) return null;
        const item = values.find(v => {
            const keys = Object.keys(v);
            return keys.some(k => k === questionConfig?.key);
        });
        return item?.[questionConfig?.key] || null;
    }, [values, questionConfig?.key]);

    // Sincronizar selectedQty con values (mostrar/ocultar selects de pasajero)
    const nextQty = currentValue != null && currentValue > 0 ? String(currentValue) : '';
    useEffect(() => {
        setSelectedQty(prev => (prev === nextQty ? prev : nextQty));
    }, [nextQty]);

    // Obtener los pasajeros seleccionados para esta categoría (referencia estable cuando está vacío)
    const currentPassengers = useMemo(() => {
        if (!values || !questionConfig?.key) return EMPTY_PASSENGERS;
        const item = values.find(v => v?.passengers && v.passengers[questionConfig?.key] !== undefined);
        const list = item?.passengers?.[questionConfig?.key];
        return list && list.length > 0 ? list : EMPTY_PASSENGERS;
    }, [values, questionConfig?.key]);

    // Sincronizar selectedPassengers con currentPassengers (solo cuando cambia el contenido)
    const prevPassengersRef = useRef(EMPTY_PASSENGERS);
    useEffect(() => {
        const prev = prevPassengersRef.current;
        const sameRef = prev === currentPassengers;
        const sameLength = prev.length === currentPassengers.length;
        const sameContent = sameLength && currentPassengers.every((p, i) => p === prev[i]);
        if (sameRef || (sameLength && sameContent)) {
            return;
        }
        prevPassengersRef.current = currentPassengers;
        if (currentPassengers.length > 0) {
            const newSelection = {};
            currentPassengers.forEach((passenger, idx) => {
                newSelection[idx] = passenger;
            });
            setSelectedPassengers(newSelection);
        } else {
            setSelectedPassengers({});
        }
    }, [currentPassengers]);

    // Generar opciones de cantidad
    const qtyOptions = useMemo(() => {
        const options = [];
        for (let i = 1; i <= totalPassengers.length; i++) {
            options.push(i);
        }
        return options;
    }, [totalPassengers.length]);

    // Manejar cambio de cantidad
    const handleQtyChange = (e) => {
        const qty = e.target.value;
        console.log('🎯 QuestionPaxQtySelector - handleQtyChange:', { qty, questionKey: questionConfig?.key });
        setSelectedQty(qty);
        setSelectedPassengers({});

        if (onChange && questionConfig?.key) {
            let auxValues = [...values];
            const keyExists = auxValues.findIndex(item => item[questionConfig.key] !== undefined);

            // Remover la selección anterior de pasajeros al cambiar cantidad
            auxValues = auxValues.map(item => {
                if (item.passengers && item.passengers[questionConfig.key] !== undefined) {
                    const { [questionConfig.key]: _, ...rest } = item.passengers;
                    return { ...item, passengers: rest };
                }
                return item;
            }).filter(item => !(item.passengers && Object.keys(item.passengers).length === 0));

            if (qty && parseInt(qty) > 0) {
                const qtyInt = parseInt(qty);
                if (keyExists >= 0) {
                    // Mantener el resto de claves del mismo ítem (otras categorías pax, etc.)
                    auxValues[keyExists] = { ...auxValues[keyExists], [questionConfig.key]: qtyInt };
                } else {
                    auxValues.push({ [questionConfig.key]: qtyInt });
                }
            } else {
                auxValues = auxValues.filter(item => item[questionConfig.key] === undefined);
            }

            console.log('📤 QuestionPaxQtySelector - onChange:', { auxValues, questionsKey: questionConfig.key });
            onChange(auxValues);
        }
    };

    // Manejar selección de pasajero
    const handlePassengerSelect = (slotIndex, passengerIndex) => {
        setSelectedPassengers(prev => {
            const newSelection = { ...prev };
            if (passengerIndex === '') {
                delete newSelection[slotIndex];
            } else {
                newSelection[slotIndex] = parseInt(passengerIndex);
            }

            // Actualizar values en el mismo callback
            if (onChange && questionConfig?.key) {
                let auxValues = [...values];

                // Remover la selección anterior de pasajeros para esta categoría
                auxValues = auxValues.map(item => {
                    if (item.passengers && item.passengers[questionConfig.key] !== undefined) {
                        const { [questionConfig.key]: _, ...rest } = item.passengers;
                        return { ...item, passengers: rest };
                    }
                    return item;
                }).filter(item => !(item.passengers && Object.keys(item.passengers).length === 0));

                // Convertir selección a array de índices
                const selectedPassengersList = Object.keys(newSelection)
                    .filter(idx => newSelection[idx] !== null && newSelection[idx] !== undefined)
                    .map(idx => parseInt(newSelection[idx]));

                if (selectedPassengersList.length > 0) {
                    // Buscar si ya existe un objeto con passengers
                    const passengersItemIndex = auxValues.findIndex(item => item.passengers !== undefined);
                    if (passengersItemIndex >= 0) {
                        auxValues[passengersItemIndex].passengers[questionConfig.key] = selectedPassengersList;
                    } else {
                        auxValues.push({ passengers: { [questionConfig.key]: selectedPassengersList } });
                    }
                }

                console.log('📤 QuestionPaxQtySelector - onChange (passengers):', {
                    auxValues,
                    questionKey: questionConfig.key,
                    selectedPassengersCount: selectedPassengersList.length
                });
                onChange(auxValues);
            }

            return newSelection;
        });
    };

    if (!questionConfig) {
        return null;
    }

    return (
        <div className='upsellings-questions-item pax-qty-selector' style={{ marginTop: '10px' }}>
            {/* Selector de cantidad */}
            <Row className='d-flex justify-content-between align-items-center mt-2'>
                <div className='col-7'>
                    <span className='text-left'>
                        {questionConfig.labelI18n?.[language] || questionConfig.label}
                    </span>
                </div>
                <div className='col-5'>
                    <Form.Control
                        as={'select'}
                        name={questionConfig.key}
                        className='text-center'
                        value={selectedQty}
                        onChange={handleQtyChange}
                    >
                        <option value="">{T('general.qty')}</option>
                        {qtyOptions.map((qty) => (
                            <option key={`qty-${questionConfig.key}-${qty}`} value={qty}>
                                {qty}
                            </option>
                        ))}
                    </Form.Control>
                </div>
            </Row>

            {/* Selectores de pasajeros individuales */}
            {selectedQty && parseInt(selectedQty) > 0 && (
                <>
                    {/* <Row className='align-items-center d-flex justify-content-between m-0 mt-4 mb-3 row'>
                        <div>
                            <span className='text-left'>
                                {T('general.select-passengers')}
                            </span>
                        </div>
                    </Row> */}

                    <Row className='mt-3 mb-4'>
                        <Col xs={12}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {Array.from({ length: parseInt(selectedQty) }).map((_, slotIdx) => {
                                    // Obtener los pasajeros ya seleccionados en otros slots
                                    const alreadySelected = Object.keys(selectedPassengers)
                                        .filter(idx => idx !== slotIdx.toString())
                                        .map(idx => selectedPassengers[idx])
                                        .filter(val => val !== null && val !== undefined);

                                    return (
                                        <div key={`passenger-slot-${slotIdx}`}>
                                            <Form.Control
                                                as={'select'}
                                                name={`passenger-${questionConfig.key}-${slotIdx}`}
                                                className='text-left'
                                                value={selectedPassengers[slotIdx] ?? ''}
                                                onChange={(e) => handlePassengerSelect(slotIdx, e.target.value)}
                                            >
                                                <option value="">{T('general.select-passenger')}</option>
                                                {totalPassengers.map((passenger, passengerIdx) => {
                                                    // Verificar si este pasajero ya está seleccionado en otro slot
                                                    const isAlreadySelected = alreadySelected.includes(passengerIdx);

                                                    // No mostrar la opción si ya está seleccionada en otro slot
                                                    if (isAlreadySelected) {
                                                        return null;
                                                    }

                                                    return (
                                                        <option key={`passenger-option-${passengerIdx}`} value={passengerIdx}>
                                                            {passenger?.name} {passenger?.surname}
                                                            {passenger?.dni && ` (${passenger?.dniType}: ${passenger?.dni})`}
                                                        </option>
                                                    );
                                                })}
                                            </Form.Control>
                                        </div>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default QuestionPaxQtySelector;
